/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Section, useMobileView } from '@tishman/components';
import React, { useMemo } from 'react';

import { usePageData } from '../../utils';

import { BuyTicketsDesktop } from './Desktop';
import { BuyTicketsMobile } from './Mobile';
import { useScrollToAnchor } from './useScrollToAnchor'; // Import the custom hook

export function BuyTicketsApp(): React.JSX.Element {
  const { allSanityTicketCategory } = usePageData<Queries.BuyTicketsQuery>();

  useScrollToAnchor(); // Use the custom hook to handle page load and scrolling

  const categories = useMemo(() => {
    //RC team wants this to be in a specific order
    const categoryOrder = [
      'top-of-the-rock-observation-deck',
      'VIP',
      'rockefeller-center-tour',
      'rockefeller-center-combo-tickets',
      'nyc-attractions',
      'the-rink',
      'holiday',
    ];

    return allSanityTicketCategory.nodes
      .filter(
        (node): node is typeof node & { slug: { current: string } } =>
          node.enabled !== false && !!node.slug?.current,
      )
      .sort((a, b) => {
        const orderA =
          categoryOrder.indexOf(a.slug.current) !== -1
            ? categoryOrder.indexOf(a.slug.current)
            : Infinity;

        const orderB =
          categoryOrder.indexOf(b.slug.current) !== -1
            ? categoryOrder.indexOf(b.slug.current)
            : Infinity;

        return orderA - orderB;
      })
      .map((category) => ({
        ...category,
        tickets:
          category.title === 'Top of the Rock'
            ? category?.tickets?.filter(
                (ticket) =>
                  ticket?.title !== 'Rock Pass' &&
                  ticket?.title !== 'Rock Center Tour',
              )
            : category.tickets,
      }));
  }, [allSanityTicketCategory]);

  // This code is used to dynamically switch between mobile and desktop views
  // based on the viewport size. It uses a state variable isMobile to track whether
  // the viewport is in mobile view or not. It replaced the components loading in a nested
  // ternary condtions that checked the state of window, sectionWidth, and mobileBreakpoint.
  // This caused a lot of rerenders and side effects.
  // New custom hook to track mobile view
  const isMobile = useMobileView();

  return (
    <Section theme="Rock Center">
      {isMobile ? (
        <BuyTicketsMobile
          categories={
            categories as unknown as Queries.BuyTicketsQuery['allSanityTicketCategory']['nodes']
          }
        />
      ) : (
        <BuyTicketsDesktop
          categories={
            categories as unknown as Queries.BuyTicketsQuery['allSanityTicketCategory']['nodes']
          }
        />
      )}
    </Section>
  );
}
